import { useEffect, useState, useRef } from "react";
import { getobj } from "../apicall/apicall";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  StyledTableCell,
  StyledTableRow,
} from "../style/style";
import "react-notifications/lib/notifications.css";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import Cookies from "js-cookie";

export default function BulkDeviceImport({ id }) {
  const [jobs, setJobs] = useState([]);
  const [loaded, setLoaded] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isDownloading, setIsDownloading] = useState({});
  const [isDownloadingLog, setIsDownloadingLog] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleRefresh = () => {
    setLoaded(true);
    fetchJobs(page);
  };

  useEffect(() => {
    fetchJobs(page);
  }, [page, id]);

  // Auto-refresh every 30 seconds for jobs that are still processing
  useEffect(() => {
    // Check if any job is still processing
    const hasProcessingJobs = jobs.some(
      job => job.status === 'PROCESSING' || job.status === 'UPLOADED'
    );
    
    if (!hasProcessingJobs) {
      return; // No processing jobs, no need to poll
    }

    // Set up interval to refresh every 30 seconds
    const interval = setInterval(() => {
      fetchJobs(page);
    }, 30000); // 30 seconds

    // Cleanup interval on unmount or when no processing jobs remain
    return () => clearInterval(interval);
  }, [jobs, page, id]); // Re-run when jobs, page, or id changes

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (["csv", "xlsx", "xls"].includes(fileExtension)) {
        setSelectedFile(file);
      } else {
        NotificationManager.error(
          "Only CSV or Excel files are allowed.",
          "Invalid File",
          3000
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      NotificationManager.error("Please select a file to upload.", "No File Selected", 3000);
      return;
    }

    if (!id) {
      NotificationManager.error("Organization ID is missing.", "Error", 3000);
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("org_id", id);

    const url = `${process.env.REACT_APP_ZEWA_URL}device/bulk-add-device/`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: "Token " + sessionStorage.getItem("token"),
          "X-CSRFToken": Cookies.get("csrftoken"),
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok || response.status === 201) {
        NotificationManager.success(
          data.Message || "File uploaded successfully. Devices are being processed.",
          "Upload Successful",
          3000
        );
        handleCloseModal();
        // Refresh the jobs list - reset to page 1 to show the new upload
        setPage(1);
        fetchJobs(1);
      } else {
        NotificationManager.error(
          data.Message || "Failed to upload file. Please try again.",
          "Upload Failed",
          3000
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      NotificationManager.error(
        "An error occurred while uploading the file. Please try again.",
        "Upload Error",
        3000
      );
    } finally {
      setIsUploading(false);
    }
  };

  const fetchJobs = (pageNum) => {
    setLoaded(true);
    // Include org_id in the URL to filter jobs for the current organization
    const url = `${process.env.REACT_APP_ZEWA_URL}user/get-bg-jobs/?page=${pageNum}${id ? `&org_id=${id}` : ''}`;
    getobj(url).then((res) => {
      if (res && res.results) {
        setJobs(res.results);
        if (res.count) {
          setTotalPages(Math.ceil(res.count / 10));
        }
      }
      setLoaded(false);
    }).catch((err) => {
      console.error("Error fetching bg jobs:", err);
      setLoaded(false);
    });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleDownload = async (job) => {
    if (!job.s3_key || !job.id) {
      console.error("No S3 key or job ID available for download");
      NotificationManager.error("Unable to download file. Missing file information.", "Download Error", 3000);
      return;
    }

    setIsDownloading((prev) => ({ ...prev, [job.id]: true }));

    try {
      // Call backend API to get presigned URL
      const url = `${process.env.REACT_APP_ZEWA_URL}device/bulk-add-device/?job_id=${job.id}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: "Token " + sessionStorage.getItem("token"),
          "X-CSRFToken": Cookies.get("csrftoken"),
        },
      });

      const data = await response.json();

      if (response.ok && data.download_url) {
        // Use presigned URL to download the file
        const link = document.createElement("a");
        link.href = data.download_url;
        link.download = data.filename || job.filename || "bulk-device-import.xlsx";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        NotificationManager.error(
          data.Message || "Failed to generate download URL. Please try again.",
          "Download Failed",
          3000
        );
      }
    } catch (error) {
      console.error("Download error:", error);
      NotificationManager.error(
        "An error occurred while downloading the file. Please try again.",
        "Download Error",
        3000
      );
    } finally {
      setIsDownloading((prev) => {
        const newObj = { ...prev };
        delete newObj[job.id];
        return newObj;
      });
    }
  };

  const handleDownloadExecutionResults = async (job) => {
    if (!job.id) {
      console.error("No job ID available for download");
      NotificationManager.error("Unable to download execution results. Missing job information.", "Download Error", 3000);
      return;
    }

    setIsDownloadingLog((prev) => ({ ...prev, [job.id]: true }));

    try {
      // Call backend API to get presigned URL for log file
      const url = `${process.env.REACT_APP_ZEWA_URL}device/bulk-add-device/?job_id=${job.id}&type=log`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: "Token " + sessionStorage.getItem("token"),
          "X-CSRFToken": Cookies.get("csrftoken"),
        },
      });

      const data = await response.json();

      if (response.ok && data.download_url) {
        // Use presigned URL to download the log file
        const link = document.createElement("a");
        link.href = data.download_url;
        // Extract filename from job or use default
        const filenameBase = job.filename ? job.filename.split('.')[0] : "bulk-device-import";
        link.download = data.filename || `${filenameBase}_execution_results.json`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        NotificationManager.error(
          data.Message || "Failed to generate download URL for execution results. Please try again.",
          "Download Failed",
          3000
        );
      }
    } catch (error) {
      console.error("Download execution results error:", error);
      NotificationManager.error(
        "An error occurred while downloading execution results. Please try again.",
        "Download Error",
        3000
      );
    } finally {
      setIsDownloadingLog((prev) => {
        const newObj = { ...prev };
        delete newObj[job.id];
        return newObj;
      });
    }
  };

  const isExecutionCompleted = (status) => {
    return status === 'COMPLETED' || status === 'FAILED' || status === 'PARTIAL_SUCCESS';
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No Data";
    const date = new Date(dateString);
    return date.toLocaleString("en-us", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "#4caf50";
      case "PROCESSING":
        return "#ff9800";
      case "FAILED":
        return "#f44336";
      case "PARTIAL_SUCCESS":
        return "#ff9800";
      case "UPLOADED":
        return "#2196f3";
      default:
        return "#757575";
    }
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          marginBottom: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          REFRESH
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadFileIcon />}
          onClick={handleOpenModal}
        >
          UPLOAD NEW FILE
        </Button>
      </Box>
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Bulk Device File</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ marginBottom: 2, fontWeight: "bold" }}>
            Required Columns:
          </Typography>
          <Box sx={{ marginBottom: 2, padding: 1, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="body2" component="div">
              • Organization Email
              <br />
              • Model
              <br />
              • Billing Type
              <br />
              • Free Period
              <br />
              • IMEI NUMBER
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 2, color: "text.secondary" }}>
            Accepted file formats: CSV, XLS, XLSX
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            style={{ width: "100%" }}
          />
          {selectedFile && (
            <Typography variant="body2" sx={{ marginTop: 1, color: "text.secondary" }}>
              Selected: {selectedFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={isUploading}>
            Cancel
          </Button>
          <LoadingButton
            onClick={handleUpload}
            loading={isUploading}
            variant="contained"
            color="primary"
            disabled={!selectedFile}
          >
            Upload
          </LoadingButton>
        </DialogActions>
      </Dialog>
      {loaded ? (
        <img
          src="https://zewa-logo-bucket.s3.amazonaws.com/logo/Spinner-1s-200px.gif"
          alt="Loading..."
        />
      ) : (
        <TableContainer>
          <Table aria-label="Bulk Device Import Jobs Table">
            <TableHead>
              <TableRow>
                <StyledTableCell>
                  <b>File Name</b>
                </StyledTableCell>
                <StyledTableCell>
                  <b>Organization</b>
                </StyledTableCell>
                <StyledTableCell>
                  <b>Uploaded By</b>
                </StyledTableCell>
                <StyledTableCell>
                  <b>Total Devices</b>
                </StyledTableCell>
                <StyledTableCell>
                  <b>Devices Processed</b>
                </StyledTableCell>
                <StyledTableCell>
                  <b>Status</b>
                </StyledTableCell>
                <StyledTableCell>
                  <b>Created At</b>
                </StyledTableCell>
                <StyledTableCell>
                  <b>Action</b>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs && jobs.length > 0 ? (
                jobs.map((job) => (
                  <StyledTableRow key={job.id}>
                    <StyledTableCell>{job.filename || "N/A"}</StyledTableCell>
                    <StyledTableCell>
                      {job.customer_name || job.customer_id || "N/A"}
                    </StyledTableCell>
                    <StyledTableCell>
                      {job.uploaded_by || "N/A"}
                    </StyledTableCell>
                    <StyledTableCell>{job.number_of_devices || 0}</StyledTableCell>
                    <StyledTableCell>
                      {job.devices_processed || 0}
                    </StyledTableCell>
                    <StyledTableCell>
                      <span
                        style={{
                          color: getStatusColor(job.status),
                          fontWeight: "bold",
                        }}
                      >
                        {job.status || "N/A"}
                      </span>
                    </StyledTableCell>
                    <StyledTableCell>{formatDate(job.created_at)}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <LoadingButton
                        size="small"
                        onClick={() => handleDownload(job)}
                        endIcon={<DownloadIcon />}
                        loading={isDownloading[job.id]}
                        loadingPosition="end"
                        variant="contained"
                        disabled={
                          !job.s3_key ||
                          (Object.keys(isDownloading).length > 0 &&
                            !isDownloading[job.id])
                        }
                      >
                          Original File
                        </LoadingButton>
                        <LoadingButton
                          size="small"
                          onClick={() => handleDownloadExecutionResults(job)}
                          endIcon={<DownloadIcon />}
                          loading={isDownloadingLog[job.id]}
                          loadingPosition="end"
                          variant="contained"
                          disabled={
                            !isExecutionCompleted(job.status) ||
                            (Object.keys(isDownloadingLog).length > 0 &&
                              !isDownloadingLog[job.id])
                          }
                        >
                          Execution results
                      </LoadingButton>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={8} align="center">
                    No bulk device import jobs found
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Pagination
        count={totalPages}
        page={page}
        variant="outlined"
        color="primary"
        onChange={handlePageChange}
        sx={{ marginTop: 2 }}
      />
      <NotificationContainer />
    </div>
  );
}

